/**
 * Scheduling's complete method/type/schema catalog lives in the shared package so HTTP,
 * backend and agent callers can use the same contract without importing backend code.
 * `Scheduling` is flat: each method takes (context, request). Channel blocks below are
 * SETTINGS, not nested service interfaces. No runtime implementation is selected here.
 */
export * from "@/shared/scheduling/contract";

import type { RecommendationType } from "@/shared";
import type { AppointmentType } from "@/shared/models/appointment";
import type { Channel, SchedulingPresentation, SchedulingSlotKind } from "@/shared/scheduling/contract";

/** Pure descriptions of authoritative facts; cannot change permissions, membership or allocations. */
export interface SchedulingPresentationPolicy {
  describeService(facts: {
    appointmentType: AppointmentType;
    appointmentPolicyTags: readonly string[];
  }): { serviceLabel: string; targetKind: SchedulingSlotKind } | undefined;
  describeRescheduleUnit(policyId: string): SchedulingPresentation | undefined;
}

export enum SchedulingSmsAvailabilityStrategy {
  AgenticProvider = "agentic_provider",
}

export enum SchedulingSmsEligibilityStrategy {
  Agentic = "agentic",
}

export enum SchedulingVoiceWorkflow {
  Deterministic = "deterministic",
}

export enum SchedulingCancellationStrategy {
  Direct = "direct",
  Staff = "staff",
}

export interface SchedulingOptionSearchConfig {
  /** Safe only for a deterministic, post-filtered chronological prefix. */
  readonly progressiveChronologicalWindows: boolean;
  /** Rank complete bounded near-term windows before searching the remainder. */
  readonly progressivePolicyRankedWindows: boolean;
  /** Search linked-unit replacements in complete bounded chronological windows. */
  readonly progressiveLinkedUnitWindows: boolean;
}

/** A single referral-matched region; customers needing multiple regions retain their own policy. */
export interface SchedulingReferralRegionConfig {
  readonly regionId: string;
  readonly excludedRegionId: string;
  /** A referral matches when its receiving provider OR receiving location is in these lists. */
  readonly providerNpis: readonly string[];
  readonly locationEhrIds: readonly string[];
}

/** Pure settings only. Presence does not enable a channel or supply a Scheduling implementation. */
export interface SchedulingCustomerConfig {
  readonly presentation?: SchedulingPresentationPolicy;
  /** Explicit per-channel cohorts for common booked reads; absent means the current path. */
  readonly appointmentQuery?: {
    readonly enabledGroupIds: Readonly<Partial<Record<Channel, readonly string[]>>>;
  };
  /** Explicit per-channel cohorts for the common location query; absent means the current path. */
  readonly locationQuery?: {
    readonly enabledGroupIds: Readonly<Partial<Record<Channel, readonly string[]>>>;
  };
  readonly policy: {
    readonly scheduleConfigPath: string;
    /** Deterministic prior-provider filtering, applied to fresh and reused eligibility results. */
    readonly requireSeenProviderFor?: readonly RecommendationType[];
  };
  readonly voice?: {
    /** Private implementation selection only; never enables an HTTP/tool route. */
    readonly workflow?: SchedulingVoiceWorkflow;
    /** Eligible groups for explicit common routes. Live tenancy also requires the runtime rollout switch. */
    readonly workflowEnabledGroupIds?: readonly string[];
    readonly cancellation?: SchedulingCancellationStrategy;
    readonly optionSearch: SchedulingOptionSearchConfig;
  };
  readonly sms?: {
    /** Implementation selection; serving still requires the explicit operation cohort below. */
    readonly eligibility?: SchedulingSmsEligibilityStrategy;
    /** Outer SMS tools only; missing cohorts retain their existing implementation. */
    readonly eligibilityEnabledGroupIds?: readonly string[];
    /** Private provider-ranking implementation only; does not enable the recommendations tool. */
    readonly availability?: SchedulingSmsAvailabilityStrategy;
    /** Outer SMS recommendations only; independent of Voice and booking rollout. */
    readonly availabilityEnabledGroupIds?: readonly string[];
    /** Confirmed SMS booking; requires common recommendations/private selections first. */
    readonly bookingEnabledGroupIds?: readonly string[];
    /** Optional customer policy; selecting SMS engines does not require referral-region narrowing. */
    readonly referralRegion?: SchedulingReferralRegionConfig;
  };
}
